import { FC, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import ReportProblemButton from '@components/common/report_problem_button';
import { Icon } from '@components/icons';
import Status from '@components/common/status';
import { IModalComponentProps } from '@context/modal_context';
import { ETransactionType } from '@xeppt/xeppt-sdk/types';
import { currencies } from '@const/currency';
import {
    copyStringToClipboard,
    formatDate,
    getFirstLettersBySpace,
    getIntentStatus,
    prettifyAmount
} from '@utils/index';
import { EIntentStatusTranslate } from '@locales/index';
import { useTranslation } from 'react-i18next';
import { useLocales } from '@hooks/helpers/useLocales';
import Typography from '@components/common/typography';
import { EPaymentIntentStatus } from '@xeppt/xeppt-sdk/types/payment-intent';
import type { TPaymentIntentWithReferenceCode } from 'src/types/reference_code';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiTransactionsService } from '@api';
import ReportProblemSubview from '../report_problem_subview';

const fullInfoStatuses = [EPaymentIntentStatus.Paid];

const ViewExtenralTransactionModal: FC<
    IModalComponentProps<{
        transaction: TPaymentIntentWithReferenceCode;
        refetchTransactions?: () => void;
    }>
> = ({ modalData, onClose }) => {
    const { i18n } = useTranslation('transaction_details');
    const { submitLocale, requestErrorLocale, requestSuccessLocale } = useLocales();
    const [showReport, setShowReport] = useState(false);

    const transaction = modalData?.transaction;
    const refetchTransactions = modalData?.refetchTransactions;

    // Hoisted before early return to comply with Rules of Hooks.
    const isRequestType = useMemo(
        () => transaction?.type !== ETransactionType.CREDIT,
        [transaction]
    );

    const getAvatar = useMemo(() => {
        return getFirstLettersBySpace(
            (transaction?.contact_first_name ?? '') + (transaction?.contact_last_name ?? '')
        );
    }, [transaction]);

    const getEmail = useMemo(() => {
        return transaction?.contact_email ?? '';
    }, [transaction]);

    const getTransactionName = useMemo(() => {
        if (transaction?.type === ETransactionType.CREDIT) {
            return 'Send money via XEPPT';
        }
        return 'Send request via XEPPT';
    }, [transaction]);

    const getSenderString = useMemo(() => {
        if (transaction?.type === ETransactionType.CREDIT) {
            return {
                neutral: `you send`,
                past: `you sent`,
                amount: transaction?.amount
            };
        }
        // Figma "Send request via XEPPT details": label is "Amount you requested" in all statuses.
        return {
            neutral: `you requested`,
            past: `you requested`,
            amount: transaction?.amount
        };
    }, [transaction]);

    const getRecipientString = useMemo(() => {
        if (transaction?.type === ETransactionType.CREDIT) {
            return {
                neutral: `${transaction?.contact_first_name ?? ''} ${transaction?.contact_last_name ?? ''} receive`,
                past: `${transaction?.contact_first_name ?? ''} ${transaction?.contact_last_name ?? ''} received`,
                amount: transaction?.amount
            };
        }
        return undefined;
    }, [transaction]);

    // Due date: derived from expires_at (available on TPaymentIntent).
    // Format: "After N days (DD Month YYYY)" matching Figma "Send request via XEPPT details".
    const dueDateString = useMemo(() => {
        if (!transaction?.expires_at) return null;
        const expiresMs = new Date(transaction.expires_at).getTime();
        const createdMs = new Date(transaction.created_at).getTime();
        const days = Math.round((expiresMs - createdMs) / (1000 * 60 * 60 * 24));
        return `After ${days} days (${formatDate(transaction.expires_at, 'DD MMMM YYYY')})`;
    }, [transaction]);

    const { handleRequest: handleCancelIntent, isLoading: isCancelIntentLoading } = useApiMutation({
        method: () => apiTransactionsService.cancelPaymentIntent(transaction?.id ?? ''),
        onSuccess: () => {
            if (refetchTransactions) refetchTransactions();
            requestSuccessLocale('cancel_intent_success');
            onClose();
        },
        onError: requestErrorLocale
    });

    if (!transaction) return null;

    if (showReport) {
        return (
            <div className={styles.wrapper}>
                <ReportProblemSubview
                    transactionRef={transaction.reference_code ?? ''}
                    onBack={() => setShowReport(false)}
                    onClose={onClose}
                />
            </div>
        );
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.txn_detail_header}>
                <Typography variant="body3" fw={600}>
                    {isRequestType ? 'Request details' : 'Transaction details'}
                </Typography>
                <button type="button" className={styles.txn_detail_close_btn} onClick={onClose}>
                    <Icon name="close" width={20} height={20} />
                </button>
            </div>
            <div className={styles.content}>
                <div className={styles.user}>
                    <div className={styles.user_avatar}>{getAvatar}</div>
                    <p className={styles.user_name}>
                        {transaction.contact_first_name} {transaction.contact_last_name}
                    </p>
                    <p className={styles.user_email}>{getEmail}</p>
                    <p className={styles.method}>External contact</p>
                    <div className={styles.divider}>
                        <Typography variant="body4">
                            <Icon
                                name={
                                    transaction.type === ETransactionType.CREDIT
                                        ? 'arrow_up'
                                        : 'arrow_down'
                                }
                            />
                            {getTransactionName}
                        </Typography>
                    </div>
                    <Status variant={getIntentStatus(transaction.status)}>
                        {transaction.status &&
                            EIntentStatusTranslate[transaction.status]?.[
                                i18n.language as 'en' | 'fr'
                            ]}
                    </Status>
                </div>
                <div className={styles.details}>
                    <p className={styles.details_title}>Details</p>
                    <div className={styles.row}>
                        <p>Date and time</p>
                        <p>{formatDate(transaction.created_at, 'DD MMMM YYYY, HH:mm')}</p>
                    </div>
                    {getSenderString && (
                        <div className={styles.row}>
                            <p>
                                Amount{' '}
                                {transaction.status !== EPaymentIntentStatus.Paid
                                    ? getSenderString.neutral
                                    : getSenderString.past}
                            </p>
                            <p className={styles.row_value}>
                                <b>
                                    {currencies[transaction.currency as 'CAD']?.symbol || '$'}
                                    {prettifyAmount(
                                        getSenderString.amount?.toFixed(2) || '0.00'
                                    )}{' '}
                                    {transaction.currency || 'CAD'}
                                </b>
                            </p>
                        </div>
                    )}
                    {fullInfoStatuses.includes(transaction.status) && getRecipientString && (
                        <div className={styles.row}>
                            <p>
                                Amount{' '}
                                {transaction.status !== EPaymentIntentStatus.Paid
                                    ? getRecipientString.neutral
                                    : getRecipientString.past}
                            </p>
                            <p className={styles.row_value}>
                                <b>
                                    {' '}
                                    {currencies[transaction.currency as 'CAD']?.symbol || '$'}
                                    {prettifyAmount(
                                        getRecipientString.amount?.toFixed(2) || '0.00'
                                    )}{' '}
                                    {transaction.currency || 'CAD'}
                                </b>
                            </p>
                        </div>
                    )}
                    {dueDateString && (
                        <div className={styles.row}>
                            <p>Due date</p>
                            <p className={styles.row_value}>{dueDateString}</p>
                        </div>
                    )}
                    {transaction.message && (
                        <div className={styles.row}>
                            <p>Message</p>
                            <p className={styles.row_value}>{transaction.message}</p>
                        </div>
                    )}
                    <div className={styles.row}>
                        <p>{isRequestType ? 'Request ID' : 'Transaction ID'}</p>
                        <p
                            className={styles.row_value}
                            onClick={() =>
                                copyStringToClipboard(transaction.reference_code ?? '')
                            }
                            style={{ cursor: 'pointer' }}>
                            {transaction.reference_code ?? '—'}
                        </p>
                    </div>
                </div>
            </div>
            <div className={styles.actions_wrapper}>
                <div className={styles.actions}>
                    <ReportProblemButton onClick={() => setShowReport(true)} />
                    {transaction.status === EPaymentIntentStatus.Pending && (
                        <Button
                            variant="red"
                            size="medium"
                            isLoading={isCancelIntentLoading}
                            onClick={() => {
                                handleCancelIntent(undefined);
                            }}>
                            {submitLocale('cancel_request')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewExtenralTransactionModal;
